import React, { useState, useRef, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { logger } from '../utils/logger';
import {
  Settings, Palette, Layout, Volume2, Moon, Sun, Monitor,
  Maximize2, Minimize2, MapPin, Languages, Eye, MessageCircle,
  Send, AlertCircle, Save, LinkIcon, MessageSquare, Upload, X, Image as ImageIcon, Globe,
  Key, Shield, RefreshCw, Plus, Trash2, Copy, Check, Webhook, Play, CheckCircle, XCircle, Download,
  Clock
} from 'lucide-react';
import ScheduleSettings from './ScheduleSettings';
import { tenantAPI, tokenManager } from '../utils/api';

const WidgetSettings = ({ config, setConfig, keywords, setKeywords, handleSaveConfig, isSaving, embedCode }) => {
  const [activeSettingsTab, setActiveSettingsTab] = React.useState('appearance');
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState();
  const [showCropper, setShowCropper] = useState(false);
  const imgRef = useRef(null);

  // Widget security state
  const [widgetSettings, setWidgetSettings] = useState(null);
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [isLoadingWidgetSettings, setIsLoadingWidgetSettings] = useState(false);
  const [isSavingDomains, setIsSavingDomains] = useState(false);
  const [isRegeneratingKeys, setIsRegeneratingKeys] = useState(false);
  const [isDownloadingPlugin, setIsDownloadingPlugin] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [widgetSettingsError, setWidgetSettingsError] = useState(null);

  // Restricted keywords input state
  const [newRestrictedKeyword, setNewRestrictedKeyword] = useState('');

  // Webhook integration state
  const [webhookSettings, setWebhookSettings] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookName, setWebhookName] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);

  const [isLoadingWebhook, setIsLoadingWebhook] = useState(false);
  const [isSavingWebhook, setIsSavingWebhook] = useState(false);
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState(null);


  // Fetch widget settings on component mount (needed for embed code on all tabs)
  useEffect(() => {
    fetchWidgetSettings();
    fetchWebhookSettings();
  }, []);

  const fetchWidgetSettings = async () => {
    const tenant = tokenManager.getTenant();
    if (!tenant?.id) {
      setWidgetSettingsError('Unable to load tenant information');
      return;
    }

    setIsLoadingWidgetSettings(true);
    setWidgetSettingsError(null);

    try {
      const settings = await tenantAPI.getWidgetSettings(tenant.id);
      setWidgetSettings(settings);
      setAllowedDomains(settings.allowed_domains || []);
    } catch (error) {
      logger.error('Failed to fetch widget settings:', error);
      setWidgetSettingsError('Failed to load widget settings');
    } finally {
      setIsLoadingWidgetSettings(false);
    }
  };

  const handleAddDomain = () => {
    if (!newDomain.trim()) return;

    // Basic domain validation
    const domain = newDomain.trim().toLowerCase();
    if (!allowedDomains.includes(domain)) {
      setAllowedDomains([...allowedDomains, domain]);
    }
    setNewDomain('');
  };

  const handleRemoveDomain = (domainToRemove) => {
    setAllowedDomains(allowedDomains.filter(d => d !== domainToRemove));
  };

  const handleSaveAllowedDomains = async () => {
    const tenant = tokenManager.getTenant();
    if (!tenant?.id) return;

    setIsSavingDomains(true);
    try {
      await tenantAPI.updateAllowedDomains(tenant.id, allowedDomains);
      alert('Allowed domains saved successfully!');
    } catch (error) {
      logger.error('Failed to save allowed domains:', error);
      alert('Failed to save allowed domains');
    } finally {
      setIsSavingDomains(false);
    }
  };

  const handleRegenerateKeys = async () => {
    const tenant = tokenManager.getTenant();
    if (!tenant?.id) return;

    if (!window.confirm('WARNING: This will invalidate ALL existing widget integrations. All embedded widgets will need to be updated with the new public key. Are you sure you want to continue?')) {
      return;
    }

    setIsRegeneratingKeys(true);
    try {
      const result = await tenantAPI.regenerateWidgetKeys(tenant.id);
      setWidgetSettings({
        ...widgetSettings,
        widget_public_key: result.widget_public_key
      });
      alert('Widget keys regenerated successfully! Update your embed code with the new public key.');
    } catch (error) {
      logger.error('Failed to regenerate widget keys:', error);
      alert('Failed to regenerate widget keys');
    } finally {
      setIsRegeneratingKeys(false);
    }
  };

  const copyToClipboard = (text, keyType) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyType);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Webhook settings handlers
  const fetchWebhookSettings = async () => {
    const tenant = tokenManager.getTenant();
    if (!tenant?.id) return;

    setIsLoadingWebhook(true);
    try {
      const settings = await tenantAPI.getWebhookSettings(tenant.id);
      setWebhookSettings(settings);
      setWebhookUrl(settings.webhook_url || '');
      setWebhookName(settings.webhook_name || '');
      setWebhookEnabled(settings.webhook_enabled || false);
    } catch (error) {
      logger.error('Failed to fetch webhook settings:', error);
    } finally {
      setIsLoadingWebhook(false);
    }
  };

  const handleSaveWebhookSettings = async () => {
    const tenant = tokenManager.getTenant();
    if (!tenant?.id) return;

    setIsSavingWebhook(true);
    setWebhookTestResult(null);
    try {
      const result = await tenantAPI.updateWebhookSettings(tenant.id, {
        webhook_url: webhookUrl,
        webhook_name: webhookName,
        webhook_enabled: webhookEnabled
      });
      setWebhookSettings(result);
      alert('Webhook settings saved successfully!');
    } catch (error) {
      logger.error('Failed to save webhook settings:', error);
      alert('Failed to save webhook settings');
    } finally {
      setIsSavingWebhook(false);
    }
  };

  const handleTestWebhook = async () => {
    const tenant = tokenManager.getTenant();
    if (!tenant?.id || !webhookUrl) return;

    setIsTestingWebhook(true);
    setWebhookTestResult(null);
    try {
      const result = await tenantAPI.testWebhook(tenant.id);
      setWebhookTestResult(result);
    } catch (error) {
      logger.error('Webhook test failed:', error);
      setWebhookTestResult({
        success: false,
        message: error.response?.data?.message || 'Failed to test webhook'
      });
    } finally {
      setIsTestingWebhook(false);
    }
  };

  // Download pre-configured WordPress plugin
  const handleDownloadWordPressPlugin = async () => {
    const tenant = tokenManager.getTenant();
    if (!tenant?.id) {
      alert('Unable to get tenant information');
      return;
    }

    setIsDownloadingPlugin(true);
    try {
      const widgetUrl = window.location.origin;
      const blob = await tenantAPI.downloadWordPressPlugin(tenant.id, widgetUrl);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `gate6-chat-widget-${tenant.id.substring(0, 8)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Failed to download WordPress plugin:', error);
      alert('Failed to download WordPress plugin. Please try again.');
    } finally {
      setIsDownloadingPlugin(false);
    }
  };

  // Generate secure embed code with widget public key
  const getSecureEmbedCode = () => {
    const tenant = tokenManager.getTenant();
    if (!widgetSettings?.widget_public_key || !tenant?.id) {
      return embedCode; // Fallback to regular embed code
    }

    return `<!-- G6th Sense Chat Widget -->
<script>
  window.G6thSenseConfig = {
    tenantId: "${tenant.id}",
    widgetPublicKey: "${widgetSettings.widget_public_key}"
  };
</script>
<script src="${window.location.origin}/widget-embed.js" async></script>`;
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
        setShowCropper(true);
        // Set initial crop to center
        setCrop({
          unit: '%',
          width: 50,
          height: 50,
          x: 25,
          y: 25
        });
      });
      reader.readAsDataURL(file);
    }
  };

  const handleCropSave = async () => {
    if (!imgRef.current || !crop) return;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelCrop = {
      x: crop.x * scaleX,
      y: crop.y * scaleY,
      width: crop.width * scaleX,
      height: crop.height * scaleY
    };

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result;
        setConfig({ ...config, logo_url: base64data });
        setShowCropper(false);
        setImageSrc(null);
        setCrop(undefined);
      };
    }, 'image/png');
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageSrc(null);
    setCrop(undefined);
  };

  const positionOptions = [
    { value: 'bottom-right', label: 'Bottom Right', icon: '↘️' },
    { value: 'bottom-left', label: 'Bottom Left', icon: '↙️' },
    { value: 'top-right', label: 'Top Right', icon: '↗️' },
    { value: 'top-left', label: 'Top Left', icon: '↖️' }
  ];

  const sizeOptions = [
    { value: 'small', label: 'Small', desc: '320x450px' },
    { value: 'medium', label: 'Medium', desc: '380x600px' },
    { value: 'large', label: 'Large', desc: '450x700px' }
  ];

  // Helper function to get background style based on color_style setting
  const getBackgroundStyle = () => {
    if (config?.color_style === "gradient") {
      return {
        background: `linear-gradient(to bottom right, ${config?.primary_color || "var(--g6-primary)"}, ${config?.secondary_color || "var(--g6-secondary)"})`
      };
    }
    return {
      backgroundColor: config?.primary_color || "var(--g6-primary)"
    };
  };

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'auto', label: 'Auto', icon: Monitor }
  ];

  const borderRadiusOptions = [
    { value: 'square', label: 'Square', radius: '0px' },
    { value: 'rounded', label: 'Rounded', radius: '12px' },
    { value: 'pill', label: 'Pill', radius: '24px' }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-[11fr_9fr] gap-6">
        {/* Left Column - Settings */}
        <div className="space-y-6">
          {/* Settings Tab Navigation */}
          <Card className="border-2 shadow-lg">
            <CardContent className="p-2">
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setActiveSettingsTab('appearance')}
                  className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${activeSettingsTab === 'appearance'
                    ? 'bg-[var(--g6-primary)] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <Palette className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline truncate">Appearance</span>
                </button>
                <button
                  onClick={() => setActiveSettingsTab('position')}
                  className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${activeSettingsTab === 'position'
                    ? 'bg-[var(--g6-primary)] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <Layout className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline truncate">Position</span>
                </button>
                <button
                  onClick={() => setActiveSettingsTab('behavior')}
                  className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${activeSettingsTab === 'behavior'
                    ? 'bg-[var(--g6-primary)] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <MessageSquare className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline truncate">Behavior</span>
                </button>
                <button
                  onClick={() => setActiveSettingsTab('security')}
                  className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${activeSettingsTab === 'security'
                    ? 'bg-[var(--g6-primary)] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <Shield className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline truncate">Security</span>
                </button>
                <button
                  onClick={() => setActiveSettingsTab('schedule')}
                  className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg font-medium text-sm transition-all ${activeSettingsTab === 'schedule'
                    ? 'bg-[var(--g6-primary)] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline truncate">Schedule</span>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Appearance Settings */}
          {activeSettingsTab === 'appearance' && (
            <>
              <Card className="border-2 border-gray-100 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="w-5 h-5 text-purple-600" />
                    Colors & Branding
                  </CardTitle>
                  <CardDescription>Customize your widget's visual identity</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Primary Color</label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="color"
                        value={config?.primary_color || "var(--g6-primary)"}
                        onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                        className="w-20 h-12 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={config?.primary_color || "var(--g6-primary)"}
                        onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                        className="flex-1 font-mono"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Chat button, send button, user messages</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Secondary Color</label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="color"
                        value={config?.secondary_color || "var(--g6-secondary)"}
                        onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
                        className="w-20 h-12 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={config?.secondary_color || "var(--g6-secondary)"}
                        onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
                        className="flex-1 font-mono"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Widget header, agent messages</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Color Style</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setConfig({ ...config, color_style: "solid" })}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${config?.color_style === "solid"
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                          }`}
                      >
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div
                            className="w-8 h-8 rounded-lg"
                            style={{ backgroundColor: config?.primary_color || "var(--g6-primary)" }}
                          />
                          <span className="text-sm font-semibold">Solid</span>
                        </div>
                        <p className="text-xs text-gray-500">Single color</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfig({ ...config, color_style: "gradient" })}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${config?.color_style === "gradient"
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                          }`}
                      >
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <div
                            className="w-8 h-8 rounded-lg"
                            style={{
                              background: `linear-gradient(to right, ${config?.primary_color || "var(--g6-primary)"}, ${config?.secondary_color || "var(--g6-secondary)"})`
                            }}
                          />
                          <span className="text-sm font-semibold">Gradient</span>
                        </div>
                        <p className="text-xs text-gray-500">Blended colors</p>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Choose how colors are applied to buttons and interactive elements</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Theme</label>
                    <div className="grid grid-cols-3 gap-3">
                      {themeOptions.map(option => {
                        const Icon = option.icon;
                        return (
                          <button
                            key={option.value}
                            onClick={() => setConfig({ ...config, theme: option.value })}
                            className={`p-4 rounded-lg border-2 transition-all ${config?.theme === option.value
                              ? 'border-[var(--g6-primary)] bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                              }`}
                          >
                            <Icon className={`w-6 h-6 mx-auto mb-2 ${config?.theme === option.value ? 'text-[var(--g6-primary)]' : 'text-gray-600'}`} />
                            <p className="text-sm font-medium">{option.label}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Border Radius</label>
                    <div className="grid grid-cols-3 gap-3">
                      {borderRadiusOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => setConfig({ ...config, border_radius: option.value })}
                          className={`p-4 rounded-lg border-2 transition-all ${config?.border_radius === option.value
                            ? 'border-[var(--g6-primary)] bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className={`w-12 h-12 mx-auto mb-2 bg-gray-300`} style={{ borderRadius: option.radius }}></div>
                          <p className="text-sm font-medium">{option.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Widget Logo</label>

                    {/* Current Logo Preview */}
                    {config?.logo_url && (
                      <div className="mb-4 p-4 border-2 border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600">Current Logo</span>
                          <button
                            onClick={() => setConfig({ ...config, logo_url: "" })}
                            className="text-red-500 hover:text-red-700 transition-colors"
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center justify-center bg-white p-4 rounded border">
                          <img src={config.logo_url} alt="Logo Preview" className="max-h-24 object-contain" />
                        </div>
                      </div>
                    )}

                    {/* Upload Button */}
                    <div className="space-y-3">
                      <label className="cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-[var(--g6-primary)] hover:bg-gray-50 transition-all">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                          <div className="flex flex-col items-center gap-2 text-center">
                            <Upload className="w-8 h-8 text-gray-400" />
                            <p className="text-sm font-medium text-gray-700">Upload Logo</p>
                            <p className="text-xs text-gray-500">Click to browse or drag and drop</p>
                            <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB</p>
                          </div>
                        </div>
                      </label>

                      <div className="text-center text-sm text-gray-500">or</div>

                      {/* Manual URL Input */}
                      <div>
                        <Input
                          type="text"
                          value={config?.logo_url && !config.logo_url.startsWith('data:') ? config.logo_url : ""}
                          onChange={(e) => setConfig({ ...config, logo_url: e.target.value })}
                          placeholder="Enter logo URL (e.g., /Logo.png)"
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-2">Or enter a URL or path to your logo</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Display Name</label>
                    <Input
                      type="text"
                      value={config?.display_name || config?.company_name || ""}
                      onChange={(e) => setConfig({ ...config, display_name: e.target.value })}
                      placeholder="Your Company Name"
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-2">Name displayed in the widget header (auto-populated from scraped data if empty)</p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Position & Layout Settings */}
          {activeSettingsTab === 'position' && (
            <>
              <Card className="border-2 border-gray-100 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
                  <CardTitle className="flex items-center gap-2">
                    <Layout className="w-5 h-5 text-blue-600" />
                    Position & Layout
                  </CardTitle>
                  <CardDescription>Configure where your widget appears</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Widget Position</label>
                    <div className="grid grid-cols-2 gap-3">
                      {positionOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => setConfig({ ...config, position: option.value })}
                          className={`p-4 rounded-lg border-2 transition-all ${config?.position === option.value
                            ? 'border-[var(--g6-primary)] bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="text-3xl mb-2">{option.icon}</div>
                          <p className="text-sm font-medium">{option.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Widget Size</label>
                    <div className="space-y-3">
                      {sizeOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => setConfig({ ...config, widget_size: option.value })}
                          className={`w-full p-4 rounded-lg border-2 transition-all flex items-center justify-between ${config?.widget_size === option.value
                            ? 'border-[var(--g6-primary)] bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            {option.value === 'small' && <Minimize2 className="w-5 h-5 text-gray-600" />}
                            {option.value === 'medium' && <Layout className="w-5 h-5 text-gray-600" />}
                            {option.value === 'large' && <Maximize2 className="w-5 h-5 text-gray-600" />}
                            <div className="text-left">
                              <p className="font-medium">{option.label}</p>
                              <p className="text-xs text-gray-500">{option.desc}</p>
                            </div>
                          </div>
                          {config?.widget_size === option.value && (
                            <Badge className="bg-[var(--g6-primary)]">Active</Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Chat Button Text</label>
                    <Input
                      value={config?.button_text || "Chat with us"}
                      onChange={(e) => setConfig({ ...config, button_text: e.target.value })}
                      placeholder="Chat with us"
                      className="h-11"
                    />
                    <p className="text-xs text-gray-500 mt-2">Text shown on chat bubble button</p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Behavior Settings */}
          {activeSettingsTab === 'behavior' && (
            <>
              <Card className="border-2 border-gray-100 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-green-600" />
                    Messages & Behavior
                  </CardTitle>
                  <CardDescription>Configure greetings and interaction settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Greeting Message</label>
                    <Textarea
                      value={config?.greeting_message || ""}
                      onChange={(e) => setConfig({ ...config, greeting_message: e.target.value })}
                      rows={4}
                      placeholder="Hi! How can I help you today?"
                      className="resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-2">First message users see when opening the chat</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Offline Message</label>
                    <Textarea
                      value={config?.offline_message || ""}
                      onChange={(e) => setConfig({ ...config, offline_message: e.target.value })}
                      rows={3}
                      placeholder="We're currently offline. Leave us a message!"
                      className="resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-2">Message shown when no agents are online</p>
                  </div>

                  {/* Flow Option Prompts */}
                  <div className="border-t pt-6">
                    <h4 className="text-sm font-semibold mb-4 text-gray-700 flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      Flow Option Follow-up Messages
                    </h4>
                    <p className="text-xs text-gray-500 mb-4">Configure the bot's response after user selects an option</p>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-600">
                          "I need information" - Follow-up
                        </label>
                        <Input
                          value={config?.information_prompt || ""}
                          onChange={(e) => setConfig({ ...config, information_prompt: e.target.value })}
                          placeholder="What would you like to know?"
                          className="h-10"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-600">
                          "Sales enquiry" - Follow-up
                        </label>
                        <Input
                          value={config?.sales_prompt || ""}
                          onChange={(e) => setConfig({ ...config, sales_prompt: e.target.value })}
                          placeholder="What product or service are you interested in?"
                          className="h-10"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-600">
                          "Talk to agent" - Follow-up
                        </label>
                        <Input
                          value={config?.agent_prompt || ""}
                          onChange={(e) => setConfig({ ...config, agent_prompt: e.target.value })}
                          placeholder="I'll connect you with an agent. Please provide your name first."
                          className="h-10"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-700">Agent Handoff Keywords</label>
                    <Input
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                      placeholder="agent, human, representative"
                      className="font-mono h-11"
                    />
                    <p className="text-xs text-gray-500 mt-2">Comma-separated keywords that trigger agent handoff</p>
                  </div>

                  {/* Restricted Keywords */}
                  <div className="border-t pt-6">
                    <h4 className="text-sm font-semibold mb-2 text-gray-700 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Restricted Keywords
                    </h4>
                    <p className="text-xs text-gray-500 mb-4">Keywords or phrases the chatbot will never mention or discuss. Use this for confidential projects, clients, or people.</p>

                    <div className="flex gap-2 mb-3">
                      <Input
                        value={newRestrictedKeyword}
                        onChange={(e) => setNewRestrictedKeyword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newRestrictedKeyword.trim()) {
                            e.preventDefault();
                            const current = config?.restricted_keywords?.keywords || [];
                            const cleaned = newRestrictedKeyword.trim().toLowerCase();
                            if (cleaned && !current.includes(cleaned)) {
                              setConfig({
                                ...config,
                                restricted_keywords: {
                                  ...config?.restricted_keywords,
                                  keywords: [...current, cleaned]
                                }
                              });
                            }
                            setNewRestrictedKeyword('');
                          }
                        }}
                        placeholder="Type a keyword and press Enter or click Add"
                        className="h-10 flex-1"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10 px-4"
                        onClick={() => {
                          const current = config?.restricted_keywords?.keywords || [];
                          const cleaned = newRestrictedKeyword.trim().toLowerCase();
                          if (cleaned && !current.includes(cleaned)) {
                            setConfig({
                              ...config,
                              restricted_keywords: {
                                ...config?.restricted_keywords,
                                keywords: [...current, cleaned]
                              }
                            });
                          }
                          setNewRestrictedKeyword('');
                        }}
                        disabled={!newRestrictedKeyword.trim()}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>

                    {(config?.restricted_keywords?.keywords || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(config?.restricted_keywords?.keywords || []).map((kw, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className="px-3 py-1.5 text-sm flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200"
                          >
                            {kw}
                            <X
                              className="w-3.5 h-3.5 cursor-pointer hover:text-red-900"
                              onClick={() => {
                                const updated = (config?.restricted_keywords?.keywords || []).filter((_, i) => i !== idx);
                                setConfig({
                                  ...config,
                                  restricted_keywords: {
                                    ...config?.restricted_keywords,
                                    keywords: updated
                                  }
                                });
                              }}
                            />
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600">Block Response Message</label>
                      <Textarea
                        value={config?.restricted_keywords?.block_message || ""}
                        onChange={(e) => setConfig({
                          ...config,
                          restricted_keywords: {
                            ...config?.restricted_keywords,
                            block_message: e.target.value
                          }
                        })}
                        rows={3}
                        placeholder="I don't have information about that particular topic. However, I'd be happy to help you with something else. What can I assist you with?"
                        className="resize-none"
                      />
                      <p className="text-xs text-gray-500 mt-2">Message shown when a visitor asks about a restricted keyword. Leave empty for default.</p>
                    </div>
                  </div>

                  {/* Scraping Settings */}
                  <div className="border-t pt-6">
                    <h4 className="text-sm font-semibold mb-4 text-gray-700 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Website Scraping
                    </h4>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-600">
                        Maximum Pages to Scrape
                      </label>
                      <Input
                        type="number"
                        min="1"
                        max="1000"
                        value={config?.max_pages_to_scrape || 100}
                        onChange={(e) => setConfig({ ...config, max_pages_to_scrape: parseInt(e.target.value) || 100 })}
                        placeholder="100"
                        className="h-10 w-32"
                      />
                      <p className="text-xs text-gray-500 mt-2">Maximum number of pages to crawl when scraping a website (1-1000)</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700">Features</label>

                    <div className="flex items-center justify-between p-4 border-2 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-gray-600" />
                        <div>
                          <p className="font-medium">Show Agent Photos</p>
                          <p className="text-xs text-gray-500">Display agent profile pictures</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setConfig({ ...config, show_agent_photos: !config?.show_agent_photos })}
                        className={`w-12 h-6 rounded-full transition-colors ${config?.show_agent_photos ? 'bg-[var(--g6-primary)]' : 'bg-gray-300'
                          }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-transform ${config?.show_agent_photos ? 'translate-x-6' : 'translate-x-0.5'
                          }`}></div>
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 border-2 rounded-lg">
                      <div className="flex items-center gap-3">
                        <MessageCircle className="w-5 h-5 text-gray-600" />
                        <div>
                          <p className="font-medium">Typing Indicator</p>
                          <p className="text-xs text-gray-500">Show "Agent is typing..."</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setConfig({ ...config, show_typing_indicator: !config?.show_typing_indicator })}
                        className={`w-12 h-6 rounded-full transition-colors ${config?.show_typing_indicator ? 'bg-[var(--g6-primary)]' : 'bg-gray-300'
                          }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-transform ${config?.show_typing_indicator ? 'translate-x-6' : 'translate-x-0.5'
                          }`}></div>
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 border-2 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Volume2 className="w-5 h-5 text-gray-600" />
                        <div>
                          <p className="font-medium">Sound Notifications</p>
                          <p className="text-xs text-gray-500">Play sound on new messages</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setConfig({ ...config, enable_sound: !config?.enable_sound })}
                        className={`w-12 h-6 rounded-full transition-colors ${config?.enable_sound ? 'bg-[var(--g6-primary)]' : 'bg-gray-300'
                          }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-transform ${config?.enable_sound ? 'translate-x-6' : 'translate-x-0.5'
                          }`}></div>
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Webhook Settings */}
              <Card className="border-2 border-gray-100 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                  <CardTitle className="flex items-center gap-2">
                    <Webhook className="w-5 h-5 text-purple-600" />
                    Webhook
                  </CardTitle>
                  <CardDescription>Configure external webhook for lead notifications (Slack, HubSpot, etc.)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  {isLoadingWebhook ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-500">Loading webhook settings...</span>
                    </div>
                  ) : (
                    <>
                      {/* Enable/Disable Webhook */}
                      <div className="flex items-center justify-between p-4 border-2 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Webhook className="w-5 h-5 text-gray-600" />
                          <div>
                            <p className="font-medium">Enable Webhook</p>
                            <p className="text-xs text-gray-500">Send notifications when users request to talk to an agent</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setWebhookEnabled(!webhookEnabled)}
                          className={`w-12 h-6 rounded-full transition-colors ${webhookEnabled ? 'bg-[var(--g6-primary)]' : 'bg-gray-300'}`}
                        >
                          <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-transform ${webhookEnabled ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                        </button>
                      </div>

                      {/* Webhook Name */}
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          Webhook Name
                        </label>
                        <Input
                          value={webhookName}
                          onChange={(e) => setWebhookName(e.target.value)}
                          placeholder="e.g., Slack Notifications, HubSpot Integration"
                          className="w-full"
                          disabled={!webhookEnabled}
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          A friendly name to identify this webhook integration
                        </p>
                      </div>

                      {/* Webhook URL */}
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          Webhook URL
                        </label>
                        <Input
                          value={webhookUrl}
                          onChange={(e) => setWebhookUrl(e.target.value)}
                          placeholder="https://your-webhook-endpoint.com/webhook"
                          className="w-full font-mono text-sm"
                          disabled={!webhookEnabled}
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          The URL to receive webhook notifications. Must be HTTPS for production.
                        </p>
                      </div>

                      {/* Test Webhook Button */}
                      {webhookEnabled && webhookUrl && (
                        <div className="pt-2">
                          <Button
                            onClick={handleTestWebhook}
                            disabled={isTestingWebhook || !webhookUrl}
                            variant="outline"
                            className="w-full border-purple-200 text-purple-600 hover:bg-purple-50"
                          >
                            {isTestingWebhook ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Testing...
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-2" />
                                Test Webhook
                              </>
                            )}
                          </Button>

                          {/* Test Result */}
                          {webhookTestResult && (
                            <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${webhookTestResult.success
                              ? 'bg-green-50 border border-green-200 text-green-700'
                              : 'bg-red-50 border border-red-200 text-red-700'
                              }`}>
                              {webhookTestResult.success ? (
                                <CheckCircle className="w-5 h-5" />
                              ) : (
                                <XCircle className="w-5 h-5" />
                              )}
                              <span className="text-sm">{webhookTestResult.message}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Webhook Payload Info */}
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-semibold mb-2 text-gray-700">Webhook Payload Format</h4>
                        <pre className="bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto text-xs font-mono">
{`{
  "event": "agent_requested",
  "userAttributes": {
    "default_name": "John Doe",
    "default_email": "john@example.com",
    "default_phone": "+1234567890"
  },
  "attributes": {
    "user_query": "User's message..."
  },
  "session_id": "...",
  "tenant_id": "...",
  "timestamp": "2024-01-08T12:00:00Z"
}`}
                        </pre>
                        <p className="text-xs text-gray-500 mt-2">
                          This payload is sent when a user requests to talk to a human agent.
                        </p>
                      </div>

                      {/* Save Webhook Button */}
                      <Button
                        onClick={handleSaveWebhookSettings}
                        disabled={isSavingWebhook}
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                      >
                        {isSavingWebhook ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Webhook Settings
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Security Settings */}
          {activeSettingsTab === 'security' && (
            <>
              <Card className="border-2 border-gray-100 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-red-600" />
                    Widget Keys
                  </CardTitle>
                  <CardDescription>Secure keys for widget authentication</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  {isLoadingWidgetSettings ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-500">Loading widget settings...</span>
                    </div>
                  ) : widgetSettingsError ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                      {widgetSettingsError}
                    </div>
                  ) : (
                    <>
                      {/* Widget Public Key */}
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          Widget Public Key
                        </label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 p-3 bg-gray-100 rounded-lg font-mono text-sm break-all border">
                            {widgetSettings?.widget_public_key || 'Not available'}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(widgetSettings?.widget_public_key, 'public')}
                            className="shrink-0"
                          >
                            {copiedKey === 'public' ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Use this key in your widget embed code. This key is safe to expose publicly.
                        </p>
                      </div>

                      {/* Tenant ID */}
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          Tenant ID
                        </label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 p-3 bg-gray-100 rounded-lg font-mono text-sm break-all border">
                            {tokenManager.getTenant()?.id || 'Not available'}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(tokenManager.getTenant()?.id, 'tenant')}
                            className="shrink-0"
                          >
                            {copiedKey === 'tenant' ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Your unique tenant identifier for the widget.
                        </p>
                      </div>

                      {/* Regenerate Keys Button */}
                      <div className="pt-4 border-t">
                        <Button
                          variant="outline"
                          onClick={handleRegenerateKeys}
                          disabled={isRegeneratingKeys}
                          className="w-full border-red-200 text-red-600 hover:bg-red-50"
                        >
                          {isRegeneratingKeys ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Regenerate Widget Keys
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-red-500 mt-2 text-center">
                          Warning: This will invalidate all existing widget integrations
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Allowed Domains */}
              <Card className="border-2 border-gray-100 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    Allowed Domains
                  </CardTitle>
                  <CardDescription>Control where your widget can be embedded</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <p className="text-sm text-gray-600">
                    Add domains where your widget is allowed to run. Use <code className="bg-gray-100 px-1 rounded">*.example.com</code> for subdomain wildcards.
                    Leave empty to allow all domains (not recommended for production).
                  </p>

                  {/* Add Domain Input */}
                  <div className="flex gap-2">
                    <Input
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      placeholder="example.com or *.example.com"
                      className="flex-1"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
                    />
                    <Button onClick={handleAddDomain} variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Domains List */}
                  <div className="space-y-2">
                    {allowedDomains.length === 0 ? (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        No domains configured. Widget will accept requests from any domain.
                      </div>
                    ) : (
                      allowedDomains.map((domain, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                        >
                          <span className="font-mono text-sm">{domain}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveDomain(domain)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Save Domains Button */}
                  <Button
                    onClick={handleSaveAllowedDomains}
                    disabled={isSavingDomains}
                    className="w-full bg-gradient-to-r from-blue-500 to-indigo-500"
                  >
                    {isSavingDomains ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Allowed Domains
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* WordPress Plugin Download */}
              <Card className="border-2 border-gray-100 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                  <CardTitle className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-purple-600" />
                    WordPress Plugin
                  </CardTitle>
                  <CardDescription>Download pre-configured plugin for WordPress sites</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <p className="text-sm text-gray-600">
                    Download a WordPress plugin that's automatically configured with your tenant credentials.
                    Simply upload to your WordPress site and activate - no configuration needed!
                  </p>

                  {allowedDomains.length === 0 ? (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h4 className="font-semibold text-yellow-800 text-sm mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Allowed Domains Required
                      </h4>
                      <p className="text-sm text-yellow-700">
                        You must configure at least one allowed domain above before downloading the WordPress plugin.
                        This ensures your widget only works on authorized websites.
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-semibold text-green-800 text-sm mb-2">What's included:</h4>
                      <ul className="text-sm text-green-700 space-y-1">
                        <li>• Your Tenant ID and Widget Public Key pre-configured</li>
                        <li>• Allowed domains list embedded in the plugin ({allowedDomains.length} domain{allowedDomains.length !== 1 ? 's' : ''})</li>
                        <li>• Widget position and settings controls</li>
                        <li>• Shortcode support for inline embedding</li>
                      </ul>
                    </div>
                  )}

                  <Button
                    onClick={handleDownloadWordPressPlugin}
                    disabled={isDownloadingPlugin || !widgetSettings?.widget_public_key || allowedDomains.length === 0}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDownloadingPlugin ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating Plugin...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download WordPress Plugin (.zip)
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    {allowedDomains.length === 0
                      ? "Add at least one allowed domain above to enable download"
                      : "After downloading, go to WordPress Admin → Plugins → Add New → Upload Plugin"
                    }
                  </p>
                </CardContent>
              </Card>

              {/* Integration / Embed Code - Only in Security tab */}
              <Card className="border-2 border-gray-100 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
                  <CardTitle className="flex items-center gap-2">
                    <LinkIcon className="w-5 h-5 text-blue-600" />
                    Integration
                  </CardTitle>
                  <CardDescription>Add this code snippet to your website</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-xs font-mono border-2 border-gray-700">
                    {getSecureEmbedCode()}
                  </pre>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(getSecureEmbedCode());
                      alert("Copied to clipboard!");
                    }}
                    variant="outline"
                    className="w-full mt-4 border-2"
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Copy Embed Code
                  </Button>
                </CardContent>
              </Card>
            </>
          )}

          {/* Schedule Settings */}
          {activeSettingsTab === 'schedule' && (
            <ScheduleSettings config={config} setConfig={setConfig} />
          )}

          {/* Save Button */}
          <Button
            onClick={handleSaveConfig}
            disabled={isSaving}
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[var(--g6-primary)] to-[#008A44] hover:shadow-lg transition-all duration-200"
          >
            <Save className="w-5 h-5 mr-2" />
            {isSaving ? "Saving Changes..." : "Save All Settings"}
          </Button>
        </div>

        {/* Right Column - Live Preview */}
        <div className="lg:sticky lg:top-6 h-fit">
          <Card className="border-2 border-gray-200 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-slate-50">
              <CardTitle className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                Live Preview
              </CardTitle>
              <CardDescription>Real-time preview of your widget</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {/* Widget Preview */}
              <div className="flex flex-col items-center">
                {/* Chat Widget Preview */}
                <div
                  className="w-80 bg-white shadow-2xl overflow-hidden border-2 border-gray-200 transition-all"
                  style={{
                    borderRadius: config?.border_radius === 'square' ? '0' :
                      config?.border_radius === 'pill' ? '24px' : '16px',
                    height: config?.widget_size === 'small' ? '450px' :
                      config?.widget_size === 'large' ? '600px' : '520px'
                  }}
                >
                  {/* Widget Header */}
                  <div
                    className={`p-4 border-b ${config?.theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
                        {config?.logo_url ? (
                          <img src={config.logo_url} alt="Logo" className="w-full h-full object-contain" />
                        ) : (
                          <div
                            className="w-full h-full flex items-center justify-center font-bold text-sm text-white"
                            style={getBackgroundStyle()}
                          >
                            G6
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className={`font-semibold ${config?.theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                          {config?.display_name || config?.company_name || "Assistant"}
                        </h3>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <p className={`text-xs ${config?.theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            Online
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Chat Messages */}
                  <div className={`p-4 space-y-3 bg-gray-50 overflow-y-auto ${config?.theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
                    }`} style={{ height: 'calc(100% - 140px)' }}>
                    {/* Greeting Message */}
                    <div className="flex justify-start">
                      <div className={`p-3 rounded-2xl rounded-tl-sm shadow-sm max-w-[80%] border ${config?.theme === 'dark' ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-800 border-gray-100'
                        }`}>
                        <p className="text-sm">
                          {config?.greeting_message || "Hi! How can I help you today?"}
                        </p>
                        <span className="text-xs text-gray-400 mt-1 block">Just now</span>
                      </div>
                    </div>

                    {/* Sample user message */}
                    <div className="flex justify-end">
                      <div
                        className="p-3 rounded-2xl rounded-br-sm shadow-sm max-w-[80%] text-white"
                        style={getBackgroundStyle()}
                      >
                        <p className="text-sm">I need help</p>
                        <span className="text-xs opacity-80 mt-1 block">Just now</span>
                      </div>
                    </div>

                    {/* Typing indicator */}
                    {config?.show_typing_indicator && (
                      <div className="flex justify-start">
                        <div className={`p-3 rounded-2xl rounded-tl-sm shadow-sm border ${config?.theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
                          }`}>
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Input Area */}
                  <div className={`p-3 border-t ${config?.theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Type a message..."
                        disabled
                        className={`flex-1 px-3 py-2 text-sm border rounded-lg ${config?.theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'
                          }`}
                      />
                      <button
                        disabled
                        className="p-2 rounded-lg text-white shadow-md"
                        style={getBackgroundStyle()}
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Chat Button Preview */}
                <div className="mt-4 flex justify-center">
                  <button
                    disabled
                    className={`flex items-center gap-2 ${config?.button_text ? 'px-5 py-3' : 'w-14 h-14 justify-center'} shadow-xl text-white font-medium`}
                    style={{
                      ...getBackgroundStyle(),
                      borderRadius: config?.border_radius === 'square' ? '8px' :
                        config?.border_radius === 'pill' ? '999px' : '12px'
                    }}
                  >
                    <MessageCircle className={config?.button_text ? "w-5 h-5" : "w-6 h-6"} />
                    {config?.button_text && <span className="text-sm">{config.button_text}</span>}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Cropper Modal */}
      {showCropper && imageSrc && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-gradient-to-r from-[var(--g6-primary)] to-[#008A44] p-3">
              <h3 className="text-white font-bold text-base flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Crop Logo Image
              </h3>
              <p className="text-white text-xs mt-1 opacity-80">Drag corners to resize, drag inside to move</p>
            </div>

            <div className="p-4 space-y-3">
              {/* Cropper Container */}
              <div className="flex items-center justify-center bg-gray-100 rounded-lg p-2" style={{ maxHeight: '350px', overflow: 'auto' }}>
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  className="max-h-[320px]"
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop"
                    style={{ maxHeight: '320px', maxWidth: '100%' }}
                  />
                </ReactCrop>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleCropCancel}
                  variant="outline"
                  className="flex-1 h-9 border text-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCropSave}
                  className="flex-1 h-9 bg-gradient-to-r from-[var(--g6-primary)] to-[#008A44] text-white text-sm"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetSettings;
